import { useQuery } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { api, fmtBytes, fmtDate, fmtDuration, fileName, folderOf } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";

const TABS = ["added", "updated", "removed", "failed"];

const FileList = ({ items, kind }) =>
  items.length ? (
    <ul className="divide-y divide-slate-800 max-h-[50vh] overflow-y-auto">
      {items.map((f) => (
        <li key={f.key} className="py-3 flex items-start justify-between gap-4" data-testid={`sync-detail-${kind}-item`}>
          <div className="min-w-0">
            <p className="text-sm text-slate-100 break-all">{fileName(f.key)}</p>
            <p className="font-mono text-[11px] text-slate-500 break-all">{folderOf(f.key)}</p>
            {f.error && <p className="text-xs text-red-400 mt-1 font-mono break-all">{f.error}</p>}
          </div>
          {f.chunks != null && <span className="font-mono text-xs text-slate-400 whitespace-nowrap">{f.chunks} chunks · {fmtBytes(f.size)}</span>}
        </li>
      ))}
    </ul>
  ) : <p className="py-10 text-center text-sm text-slate-500">No {kind} files in this run.</p>;

export const SyncDetailDialog = ({ runId, onClose }) => {
  const { data: run } = useQuery({ queryKey: ["sync-run", runId], enabled: !!runId, queryFn: () => api.get(`/admin/sync-runs/${runId}`).then((r) => r.data) });
  return (
    <Dialog open={!!runId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="sync-detail-modal" className="max-w-2xl bg-[#0F172A] border-slate-700 text-slate-100">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl">Sync run · {run ? fmtDate(run.started_at) : "…"}</DialogTitle>
          <DialogDescription asChild>
            <div className="flex flex-wrap items-center gap-2 pt-1 text-slate-400 text-xs">
              {run && <><StatusBadge value={run.status} /><StatusBadge value={run.trigger} />
                <span className="font-mono">{fmtDuration(run.duration_seconds)} · {run.skipped} unchanged · by {run.triggered_by || "—"}</span></>}
            </div>
          </DialogDescription>
        </DialogHeader>
        {run?.error && <p data-testid="sync-detail-error" className="text-sm text-red-400 font-mono bg-red-500/10 border border-red-500/30 rounded p-3 break-all">{run.error}</p>}
        {run && (
          <Tabs defaultValue={TABS.find((t) => run[t].length) || "added"}>
            <TabsList className="bg-[#080C14] border border-slate-800">
              {TABS.map((t) => (
                <TabsTrigger key={t} value={t} data-testid={`sync-detail-tab-${t}`} className="capitalize data-[state=active]:bg-slate-800 data-[state=active]:text-white text-slate-400">
                  {t} ({run[t].length})
                </TabsTrigger>
              ))}
            </TabsList>
            {TABS.map((t) => <TabsContent key={t} value={t}><FileList items={run[t]} kind={t} /></TabsContent>)}
          </Tabs>
        )}
      </DialogContent>
    </Dialog>
  );
};
